<script setup lang="ts">
import { useForm } from 'vee-validate';
import { computed, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';

import Checkbox from '@/components/base/form/Checkbox.vue';
import PostalAddress from '@/components/base/PostalAddress.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import { Offline } from '@/components/checkout/steps/payment/Payment.vue';
import { useDelivery } from '@/composables/useDelivery';
import { useAddressFields, useForm as formUtils } from '@/composables/useForm';
import { NutsAddress } from '@/utils/address';

const offline = defineModel<Offline>();

const store = useStore();
const { validatorPassed } = formUtils();
const { hasMultipleShipments, standardShipments } = useDelivery(store);
const { initializeAddress } = useAddressFields();
const addressFormModel = useForm();

const alternateAddress = ref<NutsAddress>(initializeAddress());
const billToShippingAddress = ref(true);

const firstShippingAddress = computed<NutsAddress | undefined>(
  () => standardShipments.value[0]?.address,
);

const billingAddress = computed(() =>
  billToShippingAddress.value ? firstShippingAddress.value : alternateAddress.value,
);

const validBillingAddress = computed(() => {
  if (billToShippingAddress.value) return true;
  return validatorPassed(addressFormModel.meta.value);
});

const showUseShippingAddress = computed(
  () => !hasMultipleShipments.value && firstShippingAddress.value?.street1,
);

watchEffect(() => {
  if (standardShipments.value.length !== 1 || !standardShipments.value[0]?.address?.street1) {
    billToShippingAddress.value = false;
  }
  if (!validBillingAddress.value) {
    offline.value = undefined;
    return;
  }
  offline.value = billingAddress.value ? { billingAddress: billingAddress.value } : undefined;
});
</script>

<template>
  <div class="w-full">
    <div v-if="showUseShippingAddress" class="mt-3">
      <Checkbox
        v-model="billToShippingAddress"
        class="text-xs font-normal align-top"
        data-test="bill-to-shipping-address-checkbox"
      >
        <div class="cursor-pointer">Use shipping address</div>
      </Checkbox>
      <PostalAddress
        v-if="billToShippingAddress"
        class="mt-2 ml-6"
        :address="firstShippingAddress"
      />
    </div>
    <AddressForm
      v-show="!billToShippingAddress"
      v-model="alternateAddress"
      :class="hasMultipleShipments ? 'mt-3' : 'mt-6'"
      hideB2BCheckbox
      :includeFormControls="false"
      :parentForm="addressFormModel"
      :stepNumber="4"
      uniqueIdSuffix="billing-address"
    />
  </div>
</template>
